// utils/musicTheory.ts
// Core music theory calculations for guitar fretboard visualization

import { NOTES, SCALE_FORMULAS, CAGED_SHAPES, CAGED_SHAPES_MINOR, STRING_TUNING_NOTE_INDICES } from './constants';
import type { Note, Scale, CagedQuality } from '../presets/presetSchema';
import type { CagedShapeKey, CagedShapeNote, ScaleDegree } from './constants';

// ================================================================================
// TYPES AND INTERFACES
// ================================================================================

export interface ScaleCalculationResult {
  readonly noteIndices: readonly number[];
  readonly intervalTypes: readonly ScaleDegree[];
}

export interface FretboardNote {
  readonly string: number;
  readonly fret: number;
  readonly note: Note;
  readonly type: ScaleDegree;
}

export interface ScaleIntersectionResult {
  readonly intersectionNotes: readonly number[];
  readonly noteClassifications: Record<number, 'intersection' | 'primary-only' | 'overlay-only' | 'neither'>;
  readonly primaryScale: readonly number[];
  readonly overlayScale: readonly number[];
}

// ================================================================================
// UTILITY FUNCTIONS
// ================================================================================

/**
 * Calculate the note index at a given string and fret
 * @param stringIndex - The string index (0-5)
 * @param fret - The fret number
 * @param tuningIndices - The tuning of the strings as indices into the NOTES array
 * @returns The note index (0-11)
 */
export function getNoteIndex(stringIndex: number, fret: number, tuningIndices: readonly number[]): number {
  return (tuningIndices[stringIndex] + fret) % 12;
}

/**
 * Get the note name at a given string and fret
 * @param stringIndex - The string index (0-5)
 * @param fret - The fret number
 * @param tuningIndices - The tuning of the strings as indices into the NOTES array
 * @returns The note name (e.g., "C", "F#")
 */
export function getNoteName(stringIndex: number, fret: number, tuningIndices: readonly number[]): Note {
  const noteIndex = getNoteIndex(stringIndex, fret, tuningIndices);
  return NOTES[noteIndex];
}

// ================================================================================
// SCALE CALCULATIONS
// ================================================================================

/**
 * Calculate all notes to display based on scale and root note
 * @param rootNote - The root note name (e.g., "C")
 * @param scaleType - The scale type (e.g., "MAJOR")
 * @returns Object with arrays of note indices and interval types
 */
export function calculateScale(rootNote: Note, scaleType: Scale): ScaleCalculationResult {
  const rootIndex = NOTES.indexOf(rootNote);
  if (rootIndex === -1) return { noteIndices: [], intervalTypes: [] };
  
  const formula = SCALE_FORMULAS[scaleType];
  if (!formula) return { noteIndices: [], intervalTypes: [] };
  
  // Calculate note indices in the scale
  const noteIndices = formula.map(interval => (rootIndex + interval) % 12);
  
  // Create a direct mapping from semitones to scale degrees
  const semitoneToScaleDegree: Record<number, ScaleDegree> = {
    0: 'R',   // Root
    1: 'm2',  // Minor 2nd
    2: 'M2',  // Major 2nd
    3: 'm3',  // Minor 3rd
    4: 'M3',  // Major 3rd
    5: 'P4',  // Perfect 4th
    6: 'd5',  // Diminished 5th
    7: 'P5',  // Perfect 5th
    8: 'm6',  // Minor 6th
    9: 'M6',  // Major 6th
    10: 'm7', // Minor 7th
    11: 'M7', // Major 7th
  };
  
  // Determine the interval type for each note in the scale
  const intervalTypes = formula.map((interval): ScaleDegree => {
    // Use the direct mapping, defaulting to 'R' for unknown intervals
    return semitoneToScaleDegree[interval] || 'R';
  });
  
  return { noteIndices, intervalTypes };
}

/**
 * Calculate all notes to display on the fretboard
 * @param rootNote - The root note name
 * @param keyQuality - The key quality (MAJOR, MINOR, etc.)
 * @returns Array of objects with string, fret, note and type
 */
export function calculateNotesToDisplay(rootNote: Note, keyQuality: Scale): readonly FretboardNote[] {
  const { noteIndices, intervalTypes } = calculateScale(rootNote, keyQuality);
  const result: FretboardNote[] = [];
  
  for (let stringIdx = 0; stringIdx < 6; stringIdx++) {
    for (let fret = 0; fret <= 15; fret++) {
      const noteIndex = getNoteIndex(stringIdx, fret, STRING_TUNING_NOTE_INDICES);
      const scaleNoteIdx = noteIndices.indexOf(noteIndex);
      
      if (scaleNoteIdx !== -1) {
        result.push({
          string: stringIdx,
          fret: fret,
          note: NOTES[noteIndex],
          type: intervalTypes[scaleNoteIdx]
        });
      }
    }
  }
  
  return result;
}

// ================================================================================
// CAGED SHAPE CALCULATIONS
// ================================================================================

/**
 * Calculate CAGED shape positions for a given root note and chord quality
 * @param rootNote - The root note name
 * @param shapeKey - The CAGED shape key (C, A, G, E, D)
 * @param chordQuality - The chord quality ('MAJOR' or 'MINOR')
 * @returns Array of notes in the shape pattern
 */
export function calculateCagedShape(rootNote: Note, shapeKey: CagedShapeKey, chordQuality: CagedQuality = 'MAJOR'): readonly CagedShapeNote[] {
  // Select the appropriate shape set based on chord quality
  const shapeSet = chordQuality === 'MINOR' ? CAGED_SHAPES_MINOR : CAGED_SHAPES;
  const shape = shapeSet[shapeKey];
  if (!shape) return [];
  
  // Find the root note index
  const rootIndex = NOTES.indexOf(rootNote);
  if (rootIndex === -1) return [];
  
  // Calculate the fret shift needed
  const rootNoteString = shape.rootNoteStringInShape;
  const rootNoteFretInShape = shape.rootNoteFretInShape;
  const openStringNote = STRING_TUNING_NOTE_INDICES[rootNoteString];
  const fretShift = (rootIndex - openStringNote + 12) % 12 - rootNoteFretInShape;
  
  // Apply the shift to all notes in the shape pattern
  return shape.pattern.map((note: CagedShapeNote) => ({
    string: note.string,
    fret: note.fret + fretShift,
    type: note.type
  }));
}

// ================================================================================
// SCALE INTERSECTION CALCULATIONS
// ================================================================================

/**
 * Calculate the intersection of notes between two scales
 * @param primaryRootNote - The root note of the primary scale
 * @param primaryQuality - The quality of the primary scale
 * @param overlayRootNote - The root note of the overlay scale
 * @param overlayQuality - The quality of the overlay scale
 * @returns Object with intersection notes and note classifications
 */
export function calculateScaleIntersection(
  primaryRootNote: Note, 
  primaryQuality: Scale, 
  overlayRootNote: Note, 
  overlayQuality: Scale
): ScaleIntersectionResult {
  // Calculate both scales
  const primaryScale = calculateScale(primaryRootNote, primaryQuality);
  const overlayScale = calculateScale(overlayRootNote, overlayQuality);
  
  // Find intersection notes (notes that appear in both scales)
  const intersectionNotes = primaryScale.noteIndices.filter(noteIndex => 
    overlayScale.noteIndices.includes(noteIndex)
  );
  
  // Classify all notes on the fretboard
  const noteClassifications: Record<number, 'intersection' | 'primary-only' | 'overlay-only' | 'neither'> = {};
  
  // Check every possible note (0-11)
  for (let noteIndex = 0; noteIndex < 12; noteIndex++) {
    const isInPrimary = primaryScale.noteIndices.includes(noteIndex);
    const isInOverlay = overlayScale.noteIndices.includes(noteIndex);
    
    if (isInPrimary && isInOverlay) {
      noteClassifications[noteIndex] = 'intersection';
    } else if (isInPrimary) {
      noteClassifications[noteIndex] = 'primary-only';
    } else if (isInOverlay) {
      noteClassifications[noteIndex] = 'overlay-only';
    } else {
      noteClassifications[noteIndex] = 'neither';
    }
  }
  
  return {
    intersectionNotes,
    noteClassifications,
    primaryScale: primaryScale.noteIndices,
    overlayScale: overlayScale.noteIndices
  };
}

// ================================================================================
// CHORD QUALITY INFERENCE
// ================================================================================

/**
 * Infer chord quality from scale quality based on music theory
 * @param scaleQuality - The scale quality (e.g., 'MAJOR', 'MINOR_TRIAD', etc.)
 * @returns The inferred chord quality ('MAJOR' or 'MINOR')
 */
export function inferChordQuality(scaleQuality: Scale): CagedQuality {
  // Define major-based scales and chords (contain major 3rd)
  const majorBased: readonly Scale[] = [
    'MAJOR', 'PENTATONIC_MAJOR', 'LYDIAN', 'MIXOLYDIAN',
    'MAJOR_TRIAD', 'DOMINANT_7TH', 'MAJOR_7TH', 'AUGMENTED_TRIAD',
    'AUGMENTED_7TH', 'AUGMENTED_MAJOR_7TH'
  ] as const;
  
  // Define minor-based scales and chords (contain minor 3rd)
  const minorBased: readonly Scale[] = [
    'MINOR', 'PENTATONIC_MINOR', 'BLUES', 'HARMONIC_MINOR',
    'MELODIC_MINOR', 'DORIAN', 'PHRYGIAN', 'LOCRIAN',
    'MINOR_TRIAD', 'MINOR_7TH', 'MINOR_MAJOR_7TH',
    'DIMINISHED_TRIAD', 'DIMINISHED_7TH', 'HALF_DIMINISHED_7TH'
  ] as const;
  
  if (majorBased.includes(scaleQuality)) {
    return 'MAJOR';
  } else if (minorBased.includes(scaleQuality)) {
    return 'MINOR';
  } else {
    // Ambiguous or atonal scales default to major
    return 'MAJOR';
  }
}
